import React, { useState } from "react";
import { Copy, Download, Megaphone, Sparkles, Instagram, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * MarketingKit — long-form marketing description + campaign brief for the
 * HeavenlyEats platform. Rendered inside the Super-Admin dashboard so the
 * platform owner can copy/paste snippets when pitching to prospective
 * restaurant tenants or briefing marketing partners.
 *
 * Content is ~1210 words, structured with H2/H3 headings, image placeholders,
 * and includes a channel-by-channel campaign kit. Bilingual audience assumed —
 * copy leads in English with the Dominican brand context woven in.
 */

const ImagePlaceholder = ({ label, note }) => (
    <div className="my-6 rounded-2xl border-2 border-dashed border-terracotta-300 bg-terracotta-50/40 px-6 py-8 text-center">
        <div className="text-terracotta-700 text-xs uppercase tracking-widest font-black mb-2">
            [Image: {label}]
        </div>
        <div className="text-xs text-earth-600 italic">Placement note: {note}</div>
    </div>
);

const CopyBtn = ({ text, label = "Copy" }) => {
    const [copied, setCopied] = useState(false);
    const doCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 1800);
        });
    };
    return (
        <button
            type="button"
            onClick={doCopy}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-earth-100 hover:bg-earth-200 text-earth-700 text-xs font-semibold"
        >
            <Copy className="w-3 h-3" /> {copied ? "Copied ✓" : label}
        </button>
    );
};

// ==================== Marketing copy (single source of truth) ====================
const OVERVIEW_ONE_LINER = "HeavenlyEats is the first restaurant SaaS built for the Caribbean and Latin American diaspora — bilingual, WhatsApp-first, and priced for independent kitchens, not chains.";

const CAMPAIGN_TAGLINE = "From cocina to cart in under a minute.";

const CAMPAIGN_INSTAGRAM = "Your kitchen. Your rules. Your storefront in 60 seconds.\n\nHeavenlyEats gives Dominican, Cuban, Puerto Rican, Jamaican and Latin chefs a bilingual online shop that connects straight to WhatsApp — no card processor required. Loyalty stamps, catering bookings, and beautiful photos included.\n\n👉 Start free at heavenlyeats.com\n\n#chefslife #comida #latinofood #dominicanfood";

const CAMPAIGN_EMAIL = "Subject: A storefront your abuela could set up in one afternoon\n\nHi [First Name],\n\nMost restaurant tech was built for chains with a Stripe account and a dedicated IT team. If that's not you — if you're the chef, the boss, the delivery coordinator and the marketing department — HeavenlyEats is for you.\n\n• Bilingual storefront that speaks to your customers\n• WhatsApp checkout — no card processor needed on day one\n• Loyalty stamps that turn 10th orders into free desserts\n• Catering booking flow with 72-hour lead time\n• A team panel so your cousin can help with orders on weekends\n\n$99/mo Basic plan, 14-day free trial, no credit card to start. See it live at heavenlyeats.com — or reply and I'll walk you through it.\n\n— The HeavenlyEats team";

// ==============================================================================

export default function MarketingKit() {
    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-16">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-black text-terracotta-700 mb-1">
                        HeavenlyEats · Marketing Kit
                    </div>
                    <h1 className="font-serif text-4xl font-black text-earth-900">
                        Platform description & campaign
                    </h1>
                    <p className="text-earth-600 text-sm mt-1">
                        Long-form copy + channel-ready campaign snippets. Copy any block below and paste into a deck, email, or ad brief.
                    </p>
                </div>
                <CopyBtn text={OVERVIEW_ONE_LINER} label="Copy one-liner" />
            </div>

            <ImagePlaceholder
                label="Hero — chef with tablet showing storefront"
                note="Full-width above the platform description. Warm terracotta-tinted photo of a chef in an apron holding a tablet that shows the HeavenlyEats storefront. Suggested placement: right after the tagline, before section 1."
            />

            {/* ============ Section 1 ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2">
                    1. The platform, in one sentence
                </h2>
                <p className="text-earth-800 leading-relaxed">
                    HeavenlyEats is the first restaurant SaaS built specifically for the Caribbean and Latin American diaspora market — bilingual out of the box, WhatsApp-first for orders, priced for independent kitchens instead of chains, and launch-ready in under an hour. Chefs get a beautiful mobile-first storefront in Spanish and English; guests can browse, order, and pay through the payment methods they already trust (cash, Lynk, Tpago, PayPal, or WhatsApp handoff) without swiping a card on an unfamiliar website.
                </p>
                <p className="text-earth-800 leading-relaxed">
                    The tagline: <span className="font-serif italic text-terracotta-700">&ldquo;{CAMPAIGN_TAGLINE}&rdquo;</span> — because most of our chefs open a laptop for the first time in months, and every extra minute is one they&rsquo;d rather spend at the stove.
                </p>
            </section>

            {/* ============ Section 2 ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2">
                    2. Who it&rsquo;s for
                </h2>
                <p className="text-earth-800 leading-relaxed">
                    HeavenlyEats is designed for independent restaurant owners, catering entrepreneurs, and home-based food businesses serving the diaspora — Dominican, Cuban, Puerto Rican, Jamaican, Colombian, Mexican, and beyond. Our flagship customer, Delicias Dominicanas in Montego Bay, serves Dominican comfort food to Jamaica&rsquo;s Dominican community. If you&rsquo;re running a business like that, this platform was designed with your Tuesday-morning problems in mind.
                </p>
                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4">Ideal fit</h3>
                <ul className="text-earth-800 leading-relaxed pl-6 list-disc space-y-1">
                    <li>Chefs doing $2K–$50K/month in orders, mostly through WhatsApp today</li>
                    <li>Bilingual or Spanish-first neighborhoods where a Latin storefront outperforms a generic one</li>
                    <li>Businesses that take cash and P2P (Lynk, Tpago, Zelle) more often than credit cards</li>
                    <li>Any kitchen where the chef is also the operations manager</li>
                </ul>
            </section>

            <ImagePlaceholder
                label="Split screen — Spanish storefront on iPhone next to English on iPad"
                note="Center-aligned inside section 2 or as a break between sections 2 and 3. Shows the same storefront in both languages side-by-side to sell the bilingual point instantly."
            />

            {/* ============ Section 3 ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2">
                    3. What it does — the tour
                </h2>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4">A storefront customers already know how to use</h3>
                <p className="text-earth-800 leading-relaxed">
                    Every tenant gets a mobile-first storefront with a beautifully typeset menu, chef bios, testimonials, and a WhatsApp checkout that produces a pre-filled order message — no one has to type twice. Menu photos are auto-optimized at the CDN edge so even guests on 3G see food that looks good.
                </p>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4">Loyalty and catering, without the enterprise price</h3>
                <p className="text-earth-800 leading-relaxed">
                    A built-in loyalty stamp system turns every 10th order into a free reward. Catering bookings use a guided wizard that captures guest count, event type, and menu preferences, with an automatic 72-hour lead-time validator so you never wake up to a &ldquo;we need 40 pastelitos tonight&rdquo; message.
                </p>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4">A team panel that grows with you</h3>
                <p className="text-earth-800 leading-relaxed">
                    Owners invite staff by email through a secure setup-link flow — no shared passwords, no sticky notes on the fridge. Roles are enforced end-to-end: staff members manage orders and menu items but cannot touch billing or settings. Every action is logged, and super-admin support has a &ldquo;log in as this user&rdquo; safety valve for emergencies.
                </p>

                <ImagePlaceholder
                    label="Feature Screenshot — admin orders view with WhatsApp preview"
                    note="Right-aligned floating image at the start of section 3. Screenshot from the admin panel showing an order card with the WhatsApp receipt open in a phone mockup beside it."
                />

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4">Payment methods your customers actually use</h3>
                <p className="text-earth-800 leading-relaxed">
                    HeavenlyEats supports the payment stack of the diaspora out of the box — cash on delivery, Jamaica&rsquo;s Lynk, the Dominican Republic&rsquo;s Tpago, PayPal, and manual WhatsApp confirmations. You never need a Stripe merchant account to serve your customers. Server-side price validation prevents any client-side tampering, so the total the customer sees is always the total that lands in your kitchen.
                </p>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4">Analytics that pay for the platform</h3>
                <p className="text-earth-800 leading-relaxed">
                    The Pro tier ships with a profitability dashboard that ties every order to its cost of goods and shows you which menu items are actually making money — not just selling the most. Chefs regularly discover that their &ldquo;bestseller&rdquo; is really a break-even loss leader while a quiet catering side dish carries 68% margin. The order calendar aggregates confirmed orders by day so you know exactly how much rice to soak on Wednesday, and the notify-list captures every customer who asked about an out-of-stock dish so you can email them the moment it&rsquo;s back.
                </p>
            </section>

            {/* ============ Section 4 ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2">
                    4. Built on real multi-tenant safety
                </h2>
                <p className="text-earth-800 leading-relaxed">
                    Every restaurant on HeavenlyEats lives in a strictly isolated data namespace. Orders, menus, testimonials, catering leads, customer waitlists — everything is scoped to a tenant identifier enforced automatically at the database layer. Two chefs on the platform cannot see each other&rsquo;s data even by accident. Admin sessions use secure httpOnly cookies, bcrypt-hashed passwords + PINs, and rate-limited login endpoints to shut down brute-force attempts.
                </p>
                <p className="text-earth-800 leading-relaxed">
                    Menu photography is stored on a global CDN with automatic format negotiation and quality-adaptive delivery — the same image that ships as a 240KB JPEG to older browsers goes out as a 40KB WebP to modern ones, and never leaves the container disk where it could vanish on a restart. Owner-notification emails route through Resend with a full audit trail, so a silently-failing pipeline surfaces immediately as an amber badge in the admin panel instead of a customer complaint two weeks later.
                </p>
            </section>

            {/* ============ Section 5 ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2">
                    5. Pricing snapshot
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose mt-4">
                    <div className="rounded-2xl border border-earth-200 bg-white p-5">
                        <div className="text-xs uppercase tracking-widest font-black text-earth-500">Basic</div>
                        <div className="font-serif text-4xl font-black text-earth-900 mt-1">$99<span className="text-lg text-earth-500 font-sans">/mo</span></div>
                        <div className="text-sm text-earth-700 mt-2">Bilingual storefront, WhatsApp checkout, loyalty stamps, testimonials, mobile-friendly admin, up to 3 team members.</div>
                    </div>
                    <div className="rounded-2xl border-2 border-terracotta-400 bg-terracotta-50/40 p-5 relative">
                        <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-black text-terracotta-700">Most popular</div>
                        <div className="text-xs uppercase tracking-widest font-black text-terracotta-700">Pro</div>
                        <div className="font-serif text-4xl font-black text-earth-900 mt-1">$189<span className="text-lg text-earth-500 font-sans">/mo</span></div>
                        <div className="text-sm text-earth-700 mt-2">Everything in Basic, plus catering flow, calendar, AI menu photos, email broadcasts, profitability dashboard, and unlimited team members.</div>
                    </div>
                </div>
                <p className="text-earth-800 leading-relaxed mt-4">
                    Every plan includes a 14-day free trial and a one-time $299 setup — no credit card required to start exploring the platform. Cancel anytime through Stripe&rsquo;s hosted billing portal.
                </p>
            </section>

            {/* ============ Section 6 - Campaign kit ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2 flex items-center gap-2">
                    <Megaphone className="w-6 h-6 text-terracotta-700" /> 6. Campaign kit
                </h2>
                <p className="text-earth-800 leading-relaxed">
                    A ready-to-use set of marketing assets you can drop into Instagram, cold outreach, and email newsletters. Every block is paste-and-go — swap in your chef&rsquo;s name and photo where noted.
                </p>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-terracotta-700" /> Master tagline
                </h3>
                <blockquote className="not-prose border-l-4 border-terracotta-400 pl-4 my-3 flex items-start justify-between gap-4">
                    <p className="font-serif italic text-xl text-earth-800">&ldquo;{CAMPAIGN_TAGLINE}&rdquo;</p>
                    <CopyBtn text={CAMPAIGN_TAGLINE} />
                </blockquote>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4 flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-terracotta-700" /> Instagram caption
                </h3>
                <div className="not-prose bg-earth-50 rounded-xl p-4 my-3">
                    <div className="flex items-center justify-end mb-2"><CopyBtn text={CAMPAIGN_INSTAGRAM} /></div>
                    <pre className="whitespace-pre-wrap text-sm text-earth-800 font-sans">{CAMPAIGN_INSTAGRAM}</pre>
                </div>

                <ImagePlaceholder
                    label="Reel storyboard — 3 clips"
                    note="For the Instagram/TikTok push: (1) chef pours sauce over rice, (2) phone showing a WhatsApp order arriving, (3) same chef handing bag to customer smiling. 15-second cut. Place inline with the Instagram caption block."
                />

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-terracotta-700" /> Cold-outreach email
                </h3>
                <div className="not-prose bg-earth-50 rounded-xl p-4 my-3">
                    <div className="flex items-center justify-end mb-2"><CopyBtn text={CAMPAIGN_EMAIL} /></div>
                    <pre className="whitespace-pre-wrap text-sm text-earth-800 font-sans">{CAMPAIGN_EMAIL}</pre>
                </div>

                <h3 className="font-serif text-lg font-bold text-earth-900 mt-4 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-terracotta-700" /> WhatsApp intro message (for chef-to-chef referrals)
                </h3>
                <div className="not-prose bg-earth-50 rounded-xl p-4 my-3">
                    <div className="flex items-center justify-end mb-2"><CopyBtn text={`Hola! I use a platform called HeavenlyEats for my restaurant — bilingual storefront, WhatsApp checkout, no card processor needed. $99/mo, 14 days free. Might be perfect for you — heavenlyeats.com`} /></div>
                    <pre className="whitespace-pre-wrap text-sm text-earth-800 font-sans">{`Hola! I use a platform called HeavenlyEats for my restaurant — bilingual storefront, WhatsApp checkout, no card processor needed. $99/mo, 14 days free. Might be perfect for you — heavenlyeats.com`}</pre>
                </div>
            </section>

            {/* ============ Section 7 - CTA ============ */}
            <section className="prose prose-earth max-w-none">
                <h2 className="font-serif text-2xl font-black text-earth-900 border-b border-earth-200 pb-2">
                    7. Call to action
                </h2>
                <p className="text-earth-800 leading-relaxed">
                    Every marketing touchpoint should land the reader on <span className="font-mono text-terracotta-700">heavenlyeats.com</span> with a single next step: start the 14-day trial. Every plan includes onboarding help and a live walkthrough with the founding team. If a prospect prefers, send them directly to the flagship storefront at <span className="font-mono text-terracotta-700">deliciasbistro.com/r/delicias</span> to see the platform in production before signing up.
                </p>
                <p className="text-earth-500 text-xs italic mt-8 pt-4 border-t border-earth-100">
                    Copy version 1.0 — approx 1,210 words. Last updated Feb 2026. Keep this document synced whenever pricing, plan features, or the master tagline change.
                </p>
            </section>
        </div>
    );
}
